import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Badge, IconButton, Menu, CircularProgress } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import styled from "styled-components";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const NotificationButton = styled(IconButton)`
  color: white !important;
`;

const NotificationItem = styled.div`
  background: white;
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f7fa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NotificationText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const NotificationTitle = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  color: #222;
  white-space: normal;
  word-break: break-word;
`;

const ShowMore = styled.span`
  color: #007bff;
  cursor: pointer;
  font-size: 0.85rem;
  margin-top: 4px;
`;

const NotificationTimestamp = styled.div`
  font-size: 0.75rem;
  color: #999;
  white-space: nowrap;
  margin-left: 8px;
  flex-shrink: 0;
`;

const NotificationIconWrapper = styled.div`
  color: #888;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
`;

const NotificationMenu = styled(Menu)`
  .MuiPaper-root {
    width: 480px;
    max-height: 600px;
    background-color: #fff;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
    border-radius: 12px;
    padding: 0;
    overflow-y: auto;
  }

  .MuiMenu-list {
    padding: 0;
  }
`;

  const API_BASE=  process.env.REACT_APP_BASE_URL;
const PAGE_LIMIT = 10;
const POLLING_INTERVAL = 5000;

const NotificationBell = ({ userId }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const menuPaperRef = useRef(null);
  const [shownToastIds, setShownToastIds] = useState(new Set());

  const showCustomNotificationToast = (message) => {
    toast(message, {
      containerId: "notification",
      className: "notification-toast",
      bodyClassName: "notification-toast-body",
    });
  };
  const fetchNotifications = async (pageNo) => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE}notifications/${userId}?page=${pageNo}&limit=${PAGE_LIMIT}`
      );

      const { data, totalPages: tp } = res.data;
      setNotifications((prev) => [...prev, ...data]);
      setTotalPages(tp);
      setHasMore(pageNo < tp);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestNotifications = async () => {
    try {
      const res = await axios.get(
        `${API_BASE}notifications/${userId}?page=1&limit=${PAGE_LIMIT}`
      );
      const { data } = res.data;

      const sorted = data.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      setNotifications((prev) => {
        const existingIds = new Set(prev.map((n) => n._id));
        const newNotifs = sorted.filter((n) => !existingIds.has(n._id));
        if (newNotifs.length > 0) {
          newNotifs.forEach((notif) => {if(notif.status === "unread")showCustomNotificationToast(notif.message)});
        }
        return newNotifs.length === 0 ? prev : [...newNotifs, ...prev];
      });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      fetchLatestNotifications();
    }, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [anchorEl]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    if (notifications.length === 0) {
      fetchNotifications(1);
    }
    markNotificationAsRead();
  };

  const handleClose = () => {
    setAnchorEl(null);
    setPage(1);
    setNotifications([]);
  };

  useEffect(() => {
    const menu = menuPaperRef.current;
    if (!menu) return;

    let debounceTimer = null;
    const handleScroll = () => {
      if (!menu || loading || !hasMore) return;

      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const nearBottom =
          menu.scrollTop + menu.clientHeight >= menu.scrollHeight - 10;
        if (nearBottom) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchNotifications(nextPage);
        }
      }, 200);
    };

    menu.addEventListener("scroll", handleScroll);
    return () => {
      menu.removeEventListener("scroll", handleScroll);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [page, loading, hasMore]);

 const markNotificationAsRead = async () => {
    const response = await axios.get(
      `${API_BASE}notifications/${userId}?page=1&limit=${PAGE_LIMIT}`
    );
     const { data } = response.data;
    const unreadNotifications = data.filter((notif) => notif.status === "unread");
    if (unreadNotifications.length === 0) return;

    const markAsRead = async () => {
      for (const notif of unreadNotifications) {
        try {
          await axios.post(`${API_BASE}notifications/readOne/${notif._id}`);
        } catch (err) {
          console.error(err);
        }
      }
    };

    markAsRead();
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  return (
    <>
      <NotificationButton onClick={handleOpen}>
        <Badge
          badgeContent={
            notifications.filter((n) => n.status === "unread").length > 9
              ? "9+"
              : notifications.filter((n) => n.status === "unread").length
          }
          color="error"
        >
          <NotificationsIcon />
        </Badge>
      </NotificationButton>

      <NotificationMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          ref: menuPaperRef,
          style: { maxHeight: 480, width: 480, overflowY: "auto" },
        }}
        MenuListProps={
          {
            // Remove ref from here
          }
        }
      >
        {notifications.length === 0 && !loading && (
          <NotificationItem>
            <NotificationText>
              <NotificationTitle>No notifications</NotificationTitle>
            </NotificationText>
          </NotificationItem>
        )}

        {notifications.map((notif) => (
          <NotificationItem key={notif._id}>
            <NotificationIconWrapper>
              <NotificationsIcon fontSize="small" />
            </NotificationIconWrapper>
            <NotificationText>
              <NotificationTitle>
                {expandedIds.has(notif._id)
                  ? notif.message
                  : notif.message.length > 50
                  ? notif.message.substring(0, 50) + "..."
                  : notif.message}
                {notif.message.length > 50 && !expandedIds.has(notif._id) && (
                  <ShowMore onClick={() => toggleExpand(notif._id)}>
                    Show more
                  </ShowMore>
                )}
              </NotificationTitle>
            </NotificationText>

            <NotificationTimestamp>
              {new Date(notif.createdAt).toLocaleString()}
            </NotificationTimestamp>
          </NotificationItem>
        ))}

        {loading && (
          <NotificationItem>
            <CircularProgress size={24} />
          </NotificationItem>
        )}
      </NotificationMenu>
    </>
  );
};

export default NotificationBell;
